import React from 'react';
import { T } from '../customer/customers.theme';
import { EditIcon, TrashIcon } from '../customer/CustomersUI';
import { avatarTint } from '../../SignupAndLogin/login.theme';
import { initialsOf, roleLabel } from './adminApi';

/**
 * The staff list.
 *
 * The phone's row, brought across: initials in a tinted circle, the name over
 * the username, and the role as a pill on the right. The colour comes from the
 * same hash the login screen's waiter tiles use, so the same person is the same
 * colour on the till whichever screen they appear on.
 *
 * The phone hides its two actions behind a tap-through sheet. There is room for
 * them here, so they sit on the row.
 */

function RowAction({ label, tone, onClick, children }) {
  const [hovered, setHovered] = React.useState(false);
  const danger = tone === 'danger';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function UserRow({ user, isSelf, onEdit, onDelete }) {
  const tint = avatarTint(user.username || user.name || '');
  const manager = user.role === 'manager';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '13px 18px',
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          background: tint.bg,
          borderRadius: T.radiusPill,
          fontSize: 14,
          fontWeight: 700,
          color: tint.ink,
          flexShrink: 0,
        }}
      >
        {initialsOf(user.name)}
      </span>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: T.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {user.name}
          {isSelf ? (
            <span style={{ fontSize: 12, fontWeight: 500, color: T.inkFaint }}> · you</span>
          ) : null}
        </span>
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>{user.username}</span>
      </div>

      <span
        style={{
          padding: '5px 12px',
          background: manager ? T.brand : T.shade,
          border: `1px solid ${manager ? T.brand : T.border}`,
          borderRadius: T.radiusPill,
          fontSize: 11.5,
          fontWeight: 700,
          color: manager ? T.surface : T.inkSoft,
          whiteSpace: 'nowrap',
        }}
      >
        {roleLabel(user.role)}
      </span>

      {/* Nobody edits or deletes themselves from here — the server refuses
          both, and a button that always fails is not a button. */}
      <span style={{ display: 'flex', gap: 2, width: 62, justifyContent: 'flex-end' }}>
        {isSelf ? null : (
          <>
            <RowAction label="Edit" onClick={() => onEdit(user)}>
              <EditIcon size={15} />
            </RowAction>
            <RowAction label="Delete" tone="danger" onClick={() => onDelete(user)}>
              <TrashIcon size={15} />
            </RowAction>
          </>
        )}
      </span>
    </div>
  );
}

export default function UserList({ users, currentUserId, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {users.map((user) => (
        <UserRow
          key={user._id}
          user={user}
          isSelf={user._id === currentUserId}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
